import { useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { ErrorNotice } from '../../components/ui/Notice';
import { useResource } from '../../hooks/useResource';
import { useVocabulary } from '../../vocab/VocabularyProvider';
import {
  COLLECTION_DEFS,
  requireCollection,
  TRANSITIONS_TAB,
  type SettingsTab,
} from './collections';
import { ModulesPanel } from './ModulesPanel';
import { fetchSettingsCapability, type CapabilityResult, type VocabRow } from './settings-api';
import { TransitionsPanel } from './TransitionsPanel';
import { useSettingsTab } from './useSettingsTab';
import { VocabularyPanel } from './VocabularyPanel';
import { gateFrom, WriteGateNotice } from './WriteGate';

/**
 * SETTINGS — the configurable vocabularies and the transition matrix.
 *
 * ⚠️ Renders no app chrome. The shell owns `App.tsx` and `components/layout/`; this is the route
 * element and nothing more. It must be inside `VocabularyProvider` (the transitions editor reads the
 * statuses and lanes from it) and `ProjectsProvider` (modules are per project).
 *
 * 🔴 THE WHOLE WRITE HALF IS GATED ON A CAPABILITY PROBE, because at the reconciled API commit the
 * write routes do not exist and the API says so itself — `GET /api/v1/settings` returns
 * `writable: false`. Every control is rendered, disabled, and accompanied by the list of endpoints
 * that have to land. ⛔ Nothing is mocked and nothing is hidden: a hidden control teaches nobody that
 * the feature is unbuilt.
 *
 * ⭐ Mobile: settings are low-frequency admin screens, so the tab strip scrolls horizontally and every
 * panel is a single column. There is no second axis anywhere on this screen — deliberately, since the
 * one thing that would have had two (the 26×26 matrix) is the thing this design refuses to render as
 * a grid.
 */
export function SettingsPage() {
  const [tab, setTab] = useSettingsTab();
  const vocab = useVocabulary();
  const { state } = useAuth();

  /**
   * ⛔ STRICT: only an AUTHENTICATED session carrying `is_admin` counts. `checking`, `unknown` and
   * `anonymous` all resolve to `false`, so the write half is disabled while we do not yet know —
   * the safe direction, and the one that cannot flash a live button at somebody who will be refused.
   */
  const isAdmin = state.status === 'authenticated' && state.user.is_admin === true;

  const capability = useResource<CapabilityResult>((signal) => fetchSettingsCapability(signal), []);
  const gate = useMemo(
    () => gateFrom(capability.data, capability.error ? capability.error.message : null, isAdmin),
    [capability.data, capability.error, isAdmin],
  );

  /** Bumped after any successful write so the shell's vocabulary re-reads what it caches. */
  const [, setRevision] = useState(0);
  const lanes = (vocab.resource.data?.lanes ?? []) as VocabRow[];

  return (
    <div className="flex h-full min-h-0 flex-col bg-[color:var(--color-surface)]">
      <header className="border-b border-[color:var(--color-line)]">
        <div className="flex flex-wrap items-center gap-3 px-3 py-2 sm:px-4">
          <h1 className="text-base font-semibold">Settings</h1>
          <span className="text-xs text-[color:var(--color-ink-muted)]">
            Configurable vocabularies — editing the set is an API call, not a migration.
          </span>
        </div>

        {/* ⭐ Horizontally scrollable rather than wrapped to three rows on a 390px screen. Every tab
            carries its text label at every width; an icon-only tab bar is a memory test. */}
        <nav aria-label="Settings sections" className="overflow-x-auto">
          <ul className="flex min-w-max gap-1 px-3 pb-2 sm:px-4">
            {[...COLLECTION_DEFS.map((def) => ({ id: def.name, label: def.title })), {
              id: TRANSITIONS_TAB,
              label: 'Transitions',
            }].map((entry) => (
              <li key={entry.id}>
                <button
                  type="button"
                  onClick={() => setTab(entry.id as SettingsTab)}
                  aria-current={tab === entry.id}
                  className={`h-8 rounded-full px-3 text-xs font-semibold whitespace-nowrap ${
                    tab === entry.id
                      ? 'bg-[color:var(--color-ink)] text-[color:var(--color-surface)]'
                      : 'bg-[color:var(--color-line)] text-[color:var(--color-ink-muted)]'
                  }`}
                >
                  {entry.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="space-y-3 px-3 pt-3 sm:px-4">
          {/*
            ⚠️ The probe FAILING is different from the probe saying no, and both are different from a
            403. `gateFrom` keeps them apart and `WriteGateNotice` prints which one this is.
          */}
          {capability.error && !capability.isStale ? (
            <ErrorNotice error={capability.error} onRetry={capability.reload} />
          ) : null}
          <WriteGateNotice gate={gate} />
        </div>

        {tab === TRANSITIONS_TAB ? (
          <TransitionsPanel gate={gate} />
        ) : tab === 'modules' ? (
          <ModulesPanel gate={gate} />
        ) : (
          <VocabularyPanel
            key={tab}
            def={requireCollection(tab)}
            gate={gate}
            lanes={lanes}
            onCollectionChanged={() => {
              setRevision((value) => value + 1);
              // ⭐ The shell caches the vocabularies for chips and icons everywhere else. A relabel
              // here that did not reload it would leave the rest of the app showing the old label
              // until a full refresh — stale looking exactly like current, one screen away.
              vocab.resource.reload();
            }}
          />
        )}
      </div>
    </div>
  );
}

export default SettingsPage;
